export interface ProblemDetails {
  type: string;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
  errors?: Record<string, string[]>;
  [extra: string]: unknown;
}

export function isProblemDetails(value: unknown): value is ProblemDetails {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate['status'] === 'number' && typeof candidate['title'] === 'string';
}

export interface ApiError {
  status: number;
  type: string;
  detail: string;
  validationErrors: Record<string, string[]> | null;
  extra: Record<string, unknown>;
  /**
   * `errorInterceptor` already moved the visitor off this screen because of this error — login on a
   * 401 that ended a session, the not-found page on a 404 from the resource the URL names.
   *
   * A caller that shows its own feedback must stay quiet when this is set: the toast would explain a
   * screen nobody is looking at, and clearing a loading flag would flash the page's empty state
   * before the new one paints. Read this instead of branching on `status` — which screens the
   * interceptor replaces is its decision to change, and a status check silently rots when it does.
   */
  screenReplaced: boolean;
}

export function isApiError(value: unknown): value is ApiError {
  return typeof value === 'object' && value !== null && 'screenReplaced' in value;
}

export function toApiError(problem: ProblemDetails): ApiError {
  const { status, title, type, detail, errors, ...extra } = problem;
  delete (extra as Record<string, unknown>)['instance'];
  return {
    status,
    type: type ?? 'about:blank',
    detail: detail ?? title,
    validationErrors: errors ?? null,
    extra,
    screenReplaced: false,
  };
}
